/**
 * Keeps the dock's two icons from animating over each other. The Hub wins, with a five second gap
 * after it. The Hub's animation is navigation-driven and cannot be deferred so it only reports.
 * ZigBot's nudge is the side that waits.
 */
export const ICON_ANIMATION_BUFFER_MS = 5000;

/** A count rather than a flag. The Hub icon renders in the dock and again in the panel header. */
let activeHubAnimations = 0;
let hubAnimationEndedAt = Number.NEGATIVE_INFINITY;

const listeners = new Set<() => void>();

const notify = (): void => listeners.forEach(listener => listener());

export const subscribeToIconAnimations = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

export const beginHubIconAnimation = (): void => {
    activeHubAnimations += 1;
    notify();
};

export const endHubIconAnimation = (): void => {
    activeHubAnimations = Math.max(0, activeHubAnimations - 1);
    if (activeHubAnimations === 0) {
        hubAnimationEndedAt = Date.now();
    }
    notify();
};

export const isHubIconAnimating = (): boolean => activeHubAnimations > 0;

/**
 * How long ZigBot must wait before it may start, or 0 when it may start now. While the Hub is still
 * playing the wait is the whole buffer over again, since the end time is not yet known.
 */
export const msUntilIconAnimationAllowed = (): number => {
    if (activeHubAnimations > 0) {
        return ICON_ANIMATION_BUFFER_MS;
    }
    const elapsed = Date.now() - hubAnimationEndedAt;
    return elapsed >= ICON_ANIMATION_BUFFER_MS ? 0 : ICON_ANIMATION_BUFFER_MS - elapsed;
};

/** Module state outlives a test file so each test starts from a known point. */
export const resetIconAnimationCoordinator = (): void => {
    activeHubAnimations = 0;
    hubAnimationEndedAt = Number.NEGATIVE_INFINITY;
    listeners.clear();
};
